import React from 'react';
import styled from 'styled-components';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';

const StyledPageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.primary}20,
    ${({ theme }) => theme.colors.secondary}20
  );
  padding: ${({ theme }) => theme.spacing.lg};
`;

const StyledOnboardingContainer = styled.div`
  width: 100%;
  max-width: 600px;
`;

const StyledTitle = styled.h1`
  text-align: center;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const StyledSubtitle = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const StyledFeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: ${({ theme }) => theme.spacing.xl} 0;
`;

const StyledFeatureItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  font-size: ${({ theme }) => theme.fontSizes.lg};

  &:before {
    content: '✓';
    background-color: ${({ theme }) => theme.colors.success};
    color: white;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-right: ${({ theme }) => theme.spacing.md};
    font-weight: bold;
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;

const StyledButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

export const OnboardingPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  // If user has already completed onboarding, redirect to dashboard
  if (user?.onboardingCompleted) {
    return <Navigate to='/dashboard' replace />;
  }

  const handleStartOnboarding = () => {
    // TODO: Implement onboarding flow
    // For now, just redirect to dashboard
    window.location.href = '/dashboard';
  };

  const handleSkipOnboarding = () => {
    // TODO: Mark onboarding as skipped and redirect
    window.location.href = '/dashboard';
  };

  return (
    <StyledPageContainer>
      <StyledOnboardingContainer>
        <Card padding='lg'>
          <CardHeader>
            <StyledTitle>{t('onboarding.title')}</StyledTitle>
            <StyledSubtitle>{t('onboarding.subtitle')}</StyledSubtitle>
          </CardHeader>
          <CardContent>
            <StyledFeatureList>
              <StyledFeatureItem>Track your income and expenses automatically</StyledFeatureItem>
              <StyledFeatureItem>Get personalized financial insights and recommendations</StyledFeatureItem>
              <StyledFeatureItem>Set and achieve your financial goals</StyledFeatureItem>
              <StyledFeatureItem>Chat with our AI financial assistant anytime</StyledFeatureItem>
              <StyledFeatureItem>Earn points and unlock achievements</StyledFeatureItem>
            </StyledFeatureList>

            <StyledButtonGroup>
              <Button variant='primary' fullWidth onClick={handleStartOnboarding}>
                {t('onboarding.complete')}
              </Button>
              <Button variant='ghost' fullWidth onClick={handleSkipOnboarding}>
                {t('onboarding.skip')}
              </Button>
            </StyledButtonGroup>
          </CardContent>
        </Card>
      </StyledOnboardingContainer>
    </StyledPageContainer>
  );
};
